import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CloudUpload, Loader2, Mic, Plus, X } from "lucide-react";
import { toHeaderSafeFileName } from "@/lib/header-safe-filename";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { toastSuccess, toastError } from "@/lib/toast-helpers";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { parseChapterTimestamp } from "@scroll/shared-utils";

interface ChapterDraft {
  id: string;
  timestamp: string;
  label: string;
}

function newChapterDraft(): ChapterDraft {
  return { id: Math.random().toString(36).slice(2, 9), timestamp: "", label: "" };
}

function buildChapterMarkers(chapters: ChapterDraft[]) {
  const rows = chapters.map((c) => ({
    timestamp: c.timestamp.trim(),
    label: c.label.trim(),
  }));
  const partialRow = rows.find((c) => (c.timestamp && !c.label) || (!c.timestamp && c.label));
  if (partialRow) {
    return { error: "Each chapter needs both a timestamp and title." };
  }
  const invalidRow = rows.find((c) => c.timestamp && parseChapterTimestamp(c.timestamp) == null);
  if (invalidRow) {
    return { error: "Please fix invalid chapter timestamps before publishing." };
  }
  return {
    markers: rows.filter((c) => c.timestamp && c.label),
  };
}

function getAudioFileDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(objectUrl);

    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : null;
      cleanup();
      resolve(duration);
    }, { once: true });
    audio.addEventListener("error", () => {
      cleanup();
      resolve(null);
    }, { once: true });
    audio.src = objectUrl;
  });
}

interface EpisodeUploadFormProps {
  onClose: () => void;
}

interface CreatorPodcast {
  id: number;
  showTitle: string;
  isDefault?: boolean;
  isEnabled?: boolean;
}

interface CreatorPodcastsResponse {
  podcasts: CreatorPodcast[];
}

export default function EpisodeUploadForm({ onClose }: EpisodeUploadFormProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [feedPostText, setFeedPostText] = useState("");
  const [description, setDescription] = useState("");
  const [chapters, setChapters] = useState<ChapterDraft[]>([]);
  const [selectedCreatorPodcastId, setSelectedCreatorPodcastId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const durationProbeIdRef = useRef(0);
  const selectedFileDurationRef = useRef<{ file: File; duration: number | null } | null>(null);
  const queryClient = useQueryClient();

  const { data: creatorPodcastsData } = useQuery<CreatorPodcastsResponse | null>({
    queryKey: ["creator-podcasts"],
    queryFn: async () => {
      const res = await fetch("/api/creator-podcasts", { credentials: "include" });
      if (res.status === 401) return null;
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to load podcast shows");
      }
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const creatorPodcasts = creatorPodcastsData?.podcasts ?? [];
  const selectedPodcast = creatorPodcasts.find((podcast) => podcast.id === selectedCreatorPodcastId) ?? null;

  useEffect(() => {
    if (creatorPodcasts.length === 0) {
      setSelectedCreatorPodcastId(null);
      return;
    }
    if (selectedCreatorPodcastId && creatorPodcasts.some((podcast) => podcast.id === selectedCreatorPodcastId)) return;
    setSelectedCreatorPodcastId((creatorPodcasts.find((podcast) => podcast.isDefault) ?? creatorPodcasts[0]).id);
  }, [creatorPodcasts, selectedCreatorPodcastId]);

  const publishMutation = useMutation({
    mutationFn: async (audio: { url: string; size: number; duration: number | null }) => {
      const { markers: chapterMarkers = [] } = buildChapterMarkers(chapters);
      const episodeTitle = title.trim();
      const res = await apiRequest("POST", "/api/posts", {
        content: feedPostText.trim() || episodeTitle,
        isPodcastEpisode: true,
        episodeTitle,
        episodeDescription: description || undefined,
        podcastAudioUrl: audio.url,
        podcastAudioSize: audio.size,
        podcastAudioDuration: audio.duration ?? undefined,
        creatorPodcastId: selectedCreatorPodcastId ?? undefined,
        chapterMarkers: chapterMarkers.length > 0 ? chapterMarkers : undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/podcasts/feed"] });
      queryClient.invalidateQueries({ queryKey: ["creator-podcasts"] });
      queryClient.invalidateQueries({ queryKey: ["creator-podcast-episodes", selectedCreatorPodcastId] });
      queryClient.invalidateQueries({ queryKey: ["/api/podcast/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/feed"] });
      toastSuccess("Episode published!");
      onClose();
    },
    onError: () => {
      toastError("Failed to publish episode. Please try again.");
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const accepted = ["audio/mpeg", "audio/mp4", "audio/wav", "audio/x-m4a"];
    if (!accepted.includes(file.type)) {
      toastError("Please select an audio file (MP3, MP4, WAV, or M4A).");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toastError("Audio file must be 50MB or smaller.");
      return;
    }
    setSelectedFile(file);
    selectedFileDurationRef.current = null;

    const probeId = ++durationProbeIdRef.current;
    void getAudioFileDuration(file).then((duration) => {
      if (durationProbeIdRef.current !== probeId) return;
      selectedFileDurationRef.current = { file, duration };
    });
  };

  const handlePublish = async () => {
    if (!selectedFile) {
      toastError("Please select an audio file.");
      return;
    }
    if (!title.trim()) {
      toastError("Please enter an episode title.");
      return;
    }
    const chapterValidation = buildChapterMarkers(chapters);
    if (chapterValidation.error) {
      toastError(chapterValidation.error);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const cachedDuration = selectedFileDurationRef.current?.file === selectedFile
        ? selectedFileDurationRef.current.duration
        : null;
      const duration = cachedDuration ?? await getAudioFileDuration(selectedFile);
      selectedFileDurationRef.current = { file: selectedFile, duration };
      const audio = await new Promise<{ url: string; size: number; duration: number | null }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        });
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            resolve({ url: data.url, size: data.size, duration });
          } else {
            reject(new Error("Audio upload failed"));
          }
        });
        xhr.addEventListener("error", () => reject(new Error("Audio upload failed")));
        xhr.timeout = 300000; // 5 minutes for large audio files
        xhr.open("POST", "/api/podcast/upload-audio");
        xhr.withCredentials = true;
        xhr.setRequestHeader("Content-Type", selectedFile.type || "audio/mpeg");
        xhr.setRequestHeader("X-File-Name", toHeaderSafeFileName(selectedFile.name));
        xhr.send(selectedFile);
      });

      await publishMutation.mutateAsync(audio);
    } catch {
      toastError("Failed to upload audio. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  if (creatorPodcastsData && creatorPodcasts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
        <Mic className="text-(--scroll-text-tertiary)" size={48} />
        <p className="text-(--scroll-text-secondary)">You haven't set up a podcast show yet.</p>
        <Link href="/podcast-setup">
          <Button className="scroll-btn-primary">Set up your show</Button>
        </Link>
      </div>
    );
  }

  const showTitle = selectedPodcast?.showTitle;

  return (
    <div className="space-y-4">
      {/* Show selector */}
      {creatorPodcasts.length > 1 ? (
        <label className="block space-y-2">
          <span className="text-(--scroll-text) font-medium block">Publish to</span>
          <select
            value={selectedCreatorPodcastId ?? ""}
            onChange={(event) => setSelectedCreatorPodcastId(Number(event.target.value))}
            disabled={isUploading}
            className="scroll-input border w-full rounded-md bg-(--scroll-bg) px-3 py-2 text-(--scroll-text) disabled:opacity-50"
          >
            {creatorPodcasts.map((podcast) => (
              <option key={podcast.id} value={podcast.id}>
                {podcast.showTitle || "Untitled podcast"}
                {podcast.isDefault ? " (default)" : ""}
              </option>
            ))}
          </select>
        </label>
      ) : showTitle ? (
        <p className="text-(--scroll-text-secondary) text-sm">
          Publishing to: <span className="text-(--scroll-text) font-medium">{showTitle}</span>
        </p>
      ) : null}
      {selectedPodcast?.isEnabled === false && (
        <p className="text-(--scroll-text-tertiary) text-xs">
          This show is disabled. The episode can be saved, but it will not appear in the RSS feed until the show is enabled.
        </p>
      )}

      {/* Audio file picker */}
      <p className="text-(--scroll-text-tertiary) text-xs">
        Web episode publishing currently accepts audio files. Use the Expo app to publish video episodes with automatic audio extraction.
      </p>
      {selectedFile ? (
        <div className="flex items-center gap-3 p-4 bg-(--scroll-bg-secondary) rounded-xl">
          <Mic className="text-(--scroll-text-secondary) shrink-0" size={20} />
          <div className="flex-1 min-w-0">
            <p className="text-(--scroll-text) text-sm font-medium truncate">{selectedFile.name}</p>
            <p className="text-(--scroll-text-tertiary) text-xs">
              {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
            </p>
          </div>
          <button
            className="text-(--scroll-text-secondary) hover:text-(--scroll-text) transition-colors text-sm"
            onClick={() => {
              durationProbeIdRef.current += 1;
              setSelectedFile(null);
              selectedFileDurationRef.current = null;
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            disabled={isUploading}
          >
            Change
          </button>
        </div>
      ) : (
        <div
          className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors border-(--scroll-border) hover:border-(--scroll-text-tertiary)"
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUpload className="text-(--scroll-text-tertiary) mx-auto mb-4" size={48} />
          <p className="text-(--scroll-text-secondary) mb-2">Click to select audio file</p>
          <p className="text-(--scroll-text-tertiary) text-sm">MP3, MP4, WAV, M4A — max 50MB</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/mpeg,audio/mp4,audio/wav,audio/x-m4a"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Episode title */}
      <div>
        <label className="text-(--scroll-text) font-medium block mb-2">Episode Title</label>
        <input
          type="text"
          placeholder="Enter episode title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isUploading}
          className="scroll-input border w-full rounded-md px-3 py-2 text-(--scroll-text) bg-(--scroll-bg) placeholder:text-(--scroll-text-tertiary) disabled:opacity-50"
        />
      </div>

      {/* Feed post */}
      <div>
        <label className="text-(--scroll-text) font-medium block mb-2">Feed Post</label>
        <Textarea
          placeholder="What should appear in the feed?"
          value={feedPostText}
          onChange={(e) => setFeedPostText(e.target.value)}
          className="scroll-input border resize-none h-20"
          disabled={isUploading}
        />
      </div>

      {/* Episode description */}
      <div>
        <label className="text-(--scroll-text) font-medium block mb-2">Description</label>
        <Textarea
          placeholder="What's this episode about?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="scroll-input border resize-none h-24"
          disabled={isUploading}
        />
      </div>

      {/* Chapters (optional) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-(--scroll-text) font-medium">Chapters <span className="text-(--scroll-text-tertiary) font-normal text-xs">— optional</span></label>
          <button
            type="button"
            onClick={() => setChapters((cs) => [...cs, newChapterDraft()])}
            disabled={isUploading || chapters.length >= 100}
            className="inline-flex items-center gap-1 text-(--scroll-text-secondary) hover:text-(--scroll-text) text-sm disabled:opacity-50"
          >
            <Plus size={14} /> Add chapter
          </button>
        </div>
        {chapters.length === 0 ? (
          <p className="text-(--scroll-text-tertiary) text-xs">
            Mark sections of your episode so listeners can jump around. Use mm:ss or h:mm:ss.
          </p>
        ) : (
          <ul className="space-y-2">
            {chapters.map((c, idx) => {
              const trimmedTs = c.timestamp.trim();
              const tsValid = trimmedTs === "" || parseChapterTimestamp(trimmedTs) != null;
              return (
                <li key={c.id} className="flex items-start gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="00:00"
                    aria-label={`Chapter ${idx + 1} timestamp`}
                    aria-invalid={!tsValid}
                    value={c.timestamp}
                    onChange={(e) =>
                      setChapters((cs) =>
                        cs.map((row) => (row.id === c.id ? { ...row, timestamp: e.target.value } : row)),
                      )
                    }
                    disabled={isUploading}
                    className={`scroll-input border w-24 rounded-md px-2 py-1.5 text-(--scroll-text) bg-(--scroll-bg) text-sm font-medium tabular-nums disabled:opacity-50 ${tsValid ? "" : "border-red-500/60"}`}
                  />
                  <input
                    type="text"
                    placeholder="Chapter title"
                    aria-label={`Chapter ${idx + 1} title`}
                    value={c.label}
                    onChange={(e) =>
                      setChapters((cs) =>
                        cs.map((row) => (row.id === c.id ? { ...row, label: e.target.value } : row)),
                      )
                    }
                    disabled={isUploading}
                    maxLength={200}
                    className="scroll-input border flex-1 rounded-md px-2 py-1.5 text-(--scroll-text) bg-(--scroll-bg) text-sm disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setChapters((cs) => cs.filter((row) => row.id !== c.id))}
                    aria-label={`Remove chapter ${idx + 1}`}
                    disabled={isUploading}
                    className="text-(--scroll-text-tertiary) hover:text-(--scroll-text) p-1.5 disabled:opacity-50"
                  >
                    <X size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Upload progress */}
      {isUploading && (
        <div className="p-4 bg-(--scroll-bg-secondary) rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="animate-spin text-(--scroll-text-secondary)" size={18} />
            <span className="text-(--scroll-text) font-medium text-sm">
              {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : "Publishing..."}
            </span>
          </div>
          <div className="h-1.5 bg-(--scroll-bg-tertiary) rounded-full overflow-hidden">
            <div
              className="h-full bg-(--scroll-text) rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Publish button */}
      <Button
        className="scroll-btn-primary w-full disabled:opacity-50"
        onClick={handlePublish}
        disabled={isUploading || !selectedFile || !title.trim() || !selectedCreatorPodcastId}
      >
        {isUploading ? "Publishing..." : "Publish Episode"}
      </Button>
    </div>
  );
}
